import { provideHttpClient, withInterceptors } from "@angular/common/http";
import {
  ApplicationConfig,
  ErrorHandler,
  isDevMode,
  provideZonelessChangeDetection,
} from "@angular/core";
import {
  provideRouter,
  Router,
  withComponentInputBinding,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
  withViewTransitions,
} from "@angular/router";
import { provideServiceWorker } from "@angular/service-worker";
import { provideTransloco } from "@jsverse/transloco";
import * as Sentry from "@sentry/angular";
import { version } from "../../package.json";
import { environment } from "../environments/environment";
import { routes } from "./app.routes";
import { tokenInterceptor } from "./core/interceptors/token.interceptor";
import { AdmefyHomeURL, AnalyticsUrlProxy, ApiURL, WebSocketsURL } from "./core/tokens";
import { TranslocoHttpLoader } from "./transloco-loader";

Sentry.init({
  dsn: environment.sentryDsn,
  integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
  release: `player@${version}`,
  environment: !isDevMode() ? "production" : "development",
  // Performance Monitoring
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: [
    "localhost",
    /^https:\/\/admefy\.com/,
    /^https:\/\/devel\.admefy\.com/,
    /^https:\/\/player\.admefy\.com/,
    /^https:\/\/player\.devel\.admefy\.com/,
  ],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export const appConfig: ApplicationConfig = {
  providers: [
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: AdmefyHomeURL,
      useValue: environment.websiteUrl,
    },
    {
      provide: ApiURL,
      useValue: environment.apiUrl,
    },
    {
      provide: WebSocketsURL,
      useValue: environment.wsAPIUrl,
    },
    {
      provide: AnalyticsUrlProxy,
      useValue: environment.analyticsProxyUrl,
    },
    provideZonelessChangeDetection(),
    provideRouter(
      routes,
      withViewTransitions(),
      withComponentInputBinding(),
      withEnabledBlockingInitialNavigation(),
      withInMemoryScrolling({
        scrollPositionRestoration: "top",
      }),
      withRouterConfig({
        paramsInheritanceStrategy: "always",
        onSameUrlNavigation: "reload",
      }),
    ),
    provideHttpClient(withInterceptors([tokenInterceptor])),
    provideServiceWorker("ngsw-worker.js", {
      enabled: environment.serviceWorkerEnabled,
      registrationStrategy: "registerWhenStable:30000",
    }),
    provideTransloco({
      config: {
        availableLangs: ["en", "es"],
        defaultLang: "es",
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
  ],
};
